<template>
  <div :class="timelineClass" v-if="tripProgress.length > 0">
        <h3 class="is-size-4">Booking Status</h3>
        <div class="b-steps is-vertical">
          <nav class="steps is-animated is-rounded mobile-compact">
            <ul class="step-items ml-0">
              <li v-for="(item, index) in tripProgress" :key="index"
                :class="{ 'step-item': true, 'is-active': item.isComplete }">
                <a class="step-link">
                  <div class="step-marker"></div>
                  <div class="step-details">
                    <span class="step-title"></span>
                  </div>
                </a>
              </li>
            </ul>
          </nav>
          <section class="step-content">
            <div v-for="(item, index) in tripProgress" :key="index" :id="`${index}-content`" :tabindex="index"
              class="step-item">
              <div class="step-details">
                <span v-if="item.timestamp"><small class="is-size-7 has-text-grey">{{ formatDateString(item.timestamp)
                    }}</small>
                  <br></span>
                <span class="step-title">{{ item.message }}<a v-if="item.slug == 'policyTriggered' && hasPolicyViolations" @click="openPolicyViolationModal"> View details.</a></span>
              </div>
            </div>
          </section>

        </div>


  </div>
</template>


<script>
import TripPolicyViolationModal from './TripPolicyViolationModal.vue';
export default {
  name: 'TripBookingProgress',
  props: {
    trip: {
      required: true,
      type: Object,
    },
  },
  data() {
    return {
      tripProgress: [],
      isCollapsed: true,
      policyRules: []
    };
  },
  methods: {
    toggleCollapse() {
      this.isCollapsed = !this.isCollapsed;
    },
    formatDateString(dateString) {
      // Parse the date string into a Date object

      if (dateString) {

        const date = new Date(dateString);

        // Extract the month, day, and year
        const month = date.getMonth() + 1; // getMonth() returns 0-based month, so add 1
        const day = date.getDate();
        const year = date.getFullYear().toString().slice(-2); // Get the last two digits of the year

        // Extract hours and minutes
        let hours = date.getHours();
        const minutes = date.getMinutes().toString().padStart(2, '0'); // Pad minutes with leading zero if needed
        const ampm = hours >= 12 ? 'pm' : 'am'; // Determine am/pm
        hours = hours % 12; // Convert 24-hour format to 12-hour format
        hours = hours ? hours : 12; // The hour '0' should be '12'

        // Format the date and time
        const formattedDate = `${month}/${day}/${year}`;
        const formattedTime = `${hours}:${minutes}${ampm}`;

        return `${formattedDate} ${formattedTime}`;
      }

    },
    openPolicyViolationModal() {
      let config = {
        parent: this,
        component: TripPolicyViolationModal,
        props: {
          violationNotes: this.trip.policy.violationNotes,
          rulesTriggered: this.trip.policy.rulesTriggered,
          recordLocator: this.trip.recordLocator,
          //policyRules: this.policyRules,
          travelerProfileRecordLocator: this.trip.travelerProfileRecordLocator,

        },
        trapFocus: true,
      };
      this.$buefy.modal.open(config);
    }
  },
  mounted() {
    if (this.trip) {
      this.tripProgress = this.trip.tripProgress;
    }
  },
  computed: {
    hasPolicyViolations() {
      return this.trip.policy.triggered == 'Y';
    },
    timelineClass() {
      const confirmedStage = this.trip.tripProgress.find((stage) => stage.slug === 'confirmed');
      if (confirmedStage && confirmedStage.isComplete) {
        return 'is-success';
      }
      if (this.trip.deniedAt) {
        return 'is-danger';
      }
      return 'is-warning';
    },
    timelineMessage() {
      const approvedStage = this.trip.tripProgress.find((stage) => stage.slug === 'approval');
      const approvalDeadline = this.trip.policy.approvalDeadline;
      if (approvedStage && !approvedStage.isComplete) {
        if (this.trip.policy.mostStrictApprovalRequired === 'HARD') {
          return `Your trip requires approval. It must be approved by ${this.formatDateString(approvalDeadline)} or it will be canceled.`;
        } else if (this.trip.policy.mostStrictApprovalRequired === 'SOFT') {
          return `Your trip is pending approval. If it is not approved by ${this.formatDateString(approvalDeadline)} it will be automatically approved and ticketed.`;
        }
      }

      if (this.trip.deniedAt) {
        return `Your trip was denied on ${this.formatDateString(this.trip.deniedAt)}.`;
      }

      if (this.trip.tripProgress.find((stage) => stage.slug === 'confirmed' && stage.isComplete)) {
          return 'Your trip has been confirmed.';
      }


      return null;
    },
  },
};

</script>

<style scoped lang="scss">
.b-steps {

  &.is-horizontal,
  &.is-vertical {
    .steps .step-items .step-item .step-marker {
      height: 1rem;
      width: 1rem;
      border-color: #b5b5b5;
    }
  }
}

.b-steps.is-horizontal {

  .steps .step-items .step-item::before {
    top: 0.5rem;
  }
}

.b-steps.is-vertical {
  .steps+.step-content {
    padding: 0 1em;
  }

  .steps+.step-content .step-item {
    padding: 0.5em 0;
    max-width: min-content;
    min-width: 100%;
  }

  .steps+.step-content .step-item .step-details {
    display: flex;
    flex-direction: column;
    justify-content: center; 
    height: 4.5em;
    line-height: 1.5;

  }

  &>.steps .step-items .step-item {
    padding: 1.5em 0;
  }
}

// Custom Colors
$is-warning: #ffe08a;
$is-success: #48c78e;
$is-danger: #f14668;

.is-warning .b-steps .step-item::before,
.is-warning .b-steps .step-item::after {
  background: linear-gradient(to left, #dbdbdb 50%, $is-warning 50%);
  background-size: 200% 100%;
  background-position: right bottom;
}

.is-warning .b-steps.is-vertical .step-item::before,
.is-warning .b-steps.is-vertical .step-item::after {
  background: linear-gradient(to top, #dbdbdb 50%, $is-warning 50%);
  background-size: 100% 200%;
  background-position: left bottom;
}

.is-warning .steps .step-items .step-item.is-active .step-marker {
  border-color: $is-warning;
  background: $is-warning;
}

.is-success .b-steps .step-item::before,
.is-success .b-steps .step-item::after {
  background: linear-gradient(to left, #dbdbdb 50%, $is-success 50%);
  background-size: 200% 100%;
  background-position: right bottom;
}

.is-success .b-steps.is-vertical .step-item::before,
.is-success .b-steps.is-vertical .step-item::after {
  background: linear-gradient(to top, #dbdbdb 50%, $is-success 50%);
  background-size: 100% 200%;
  background-position: left bottom;
}

.is-success .steps .step-items .step-item.is-active .step-marker {
  border-color: $is-success;
  background: $is-success;
}

.is-danger .b-steps .step-item::before,
.is-danger .b-steps .step-item::after {
  background: linear-gradient(to left, #dbdbdb 50%, $is-danger 50%);
  background-size: 200% 100%;
  background-position: right bottom;
}

.is-danger .b-steps.is-vertical .step-item::before,
.is-danger .b-steps.is-vertical .step-item::after {
  background: linear-gradient(to top, #dbdbdb 50%, $is-danger 50%);
  background-size: 100% 200%;
  background-position: left bottom;
}

.is-danger .steps .step-items .step-item.is-active .step-marker {
  border-color: $is-danger;
  background: $is-danger;
}
</style>